/**
 * AI APIs → Models (A7Y-5559): each provider's current catalog, with the
 * models a registry task routes to badged `In use`. Prices for models we call
 * come from our rate tables (`pricingSource: 'registry'`); the rest are list
 * prices as read on the catalog's `readAt` day. The active tab lives in the
 * URL hash so a link can open one provider.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import { isAiApiProvider, type AiApiCatalogModel, type AiApiProvider } from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { CARD_CLASSES, PROVIDER_ORDER, providerInfo } from './meta';
import { ErrorBanner } from './shared';

// Copied from `finance/PnlPage.tsx` so the two segmented controls read the same.
const TAB_TRIGGER_CLASSES =
  'px-3 py-1.5 text-sm rounded-md text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100 data-[state=active]:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400';

const ABSENT = '—';

/** `$3.00`, with up to four decimals for sub-cent per-token prices; `—` for a price the provider does not charge. */
function formatPrice(value: number | undefined): string {
  if (value === undefined || value === null || !Number.isFinite(value)) return ABSENT;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

function formatContext(tokens: number | undefined): string {
  if (!tokens) return ABSENT;
  return tokens >= 1_000_000 ? `${tokens / 1_000_000}M` : `${Math.round(tokens / 1000)}k`;
}

function ModelRow({ model }: { model: AiApiCatalogModel }) {
  const p = model.pricing ?? {};
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium text-zinc-900 dark:text-zinc-100">{model.displayName}</div>
        <div className="font-mono text-xs text-zinc-500">{model.model}</div>
      </TableCell>
      <TableCell>{model.generation}</TableCell>
      <TableCell className="tabular-nums">{formatPrice(p.inputPerMillion)}</TableCell>
      <TableCell className="tabular-nums">{formatPrice(p.cachedInputPerMillion)}</TableCell>
      <TableCell className="tabular-nums">{formatPrice(p.cacheWritePerMillion)}</TableCell>
      <TableCell className="tabular-nums">{formatPrice(p.outputPerMillion)}</TableCell>
      <TableCell className="tabular-nums">{formatPrice(p.perRequest)}</TableCell>
      <TableCell className="tabular-nums">{formatContext(model.contextWindow)}</TableCell>
      <TableCell className="max-w-56 whitespace-normal text-xs">{model.useCase}</TableCell>
      <TableCell>
        {model.inUse ? (
          <Badge
            color="green"
            title={model.tasks.length ? `Tasks: ${model.tasks.join(', ')}` : 'In use'}
          >
            In use
          </Badge>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export default function ModelsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const catalogQ = useQuery({
    queryKey: ['ai-apis', 'models'],
    queryFn: () => api.getAiApiModels(),
  });

  const fromHash = location.hash.replace(/^#/, '');
  const active: AiApiProvider = isAiApiProvider(fromHash) ? fromHash : PROVIDER_ORDER[0];
  const catalog = catalogQ.data?.data;

  return (
    <div className="px-4 py-0">
      <PageHeader title="Models" />

      {catalogQ.isError ? (
        <ErrorBanner endpoint="/admin/ai-apis/models" error={catalogQ.error} />
      ) : null}
      {catalogQ.isLoading ? (
        <div className="px-2 text-sm text-zinc-500">Loading the model catalog…</div>
      ) : null}

      {catalog ? (
        <Tabs.Root
          value={active}
          onValueChange={(value) => navigate({ hash: value }, { replace: true })}
        >
          <Tabs.List className="mb-4 inline-flex items-center gap-1 rounded-lg bg-zinc-100 dark:bg-zinc-900 p-1">
            {PROVIDER_ORDER.map((id) => (
              <Tabs.Trigger key={id} value={id} className={TAB_TRIGGER_CLASSES}>
                {providerInfo(id).label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {PROVIDER_ORDER.map((id) => {
            const entry = catalog.providers[id];
            return (
              <Tabs.Content key={id} value={id}>
                <section className={CARD_CLASSES}>
                  {entry && entry.models.length ? (
                    <Table dense striped>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Model</TableHeader>
                          <TableHeader>Generation</TableHeader>
                          <TableHeader>Input $/1M</TableHeader>
                          <TableHeader>Cached input $/1M</TableHeader>
                          <TableHeader>Cache write $/1M</TableHeader>
                          <TableHeader>Output $/1M</TableHeader>
                          <TableHeader>Per request $</TableHeader>
                          <TableHeader>Context</TableHeader>
                          <TableHeader>Use case</TableHeader>
                          <TableHeader>In use</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entry.models.map((model) => (
                          <ModelRow key={model.model} model={model} />
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-zinc-500">
                      The catalog lists no {providerInfo(id).label} models.
                    </p>
                  )}
                  {entry ? (
                    <p
                      className="mt-3 text-xs text-zinc-500 dark:text-zinc-400"
                      data-testid={`catalog-footer-${id}`}
                    >
                      Prices for models we call are read from the rate tables; others from{' '}
                      <a
                        href={entry.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        {entry.sourceUrl}
                      </a>{' '}
                      on {catalog.readAt}.
                      {entry.batchMultiplier !== undefined
                        ? ` Batch requests are billed at ×${entry.batchMultiplier}.`
                        : ''}
                    </p>
                  ) : null}
                </section>
              </Tabs.Content>
            );
          })}
        </Tabs.Root>
      ) : null}
    </div>
  );
}
